import { useState } from "react";
import { toast } from "sonner";
import Icon from "@/components/icon";
import Switch from "@/components/switch";
import TopBar from "@/components/top-bar";
import ConfirmDrawer from "@/components/confirm-drawer";
import Section from "@/components/section";
import Row, { RowDivider } from "@/components/row";
import { useSettingStore } from "@/store/setting-store";
import { isAndroidMiniapp } from "@/utils/utilities";

const Chevron = () => (
  <Icon name="chevron-left" size={18} className="text-default-300" />
);

const IMAGE_CACHE_DB = "image-cache";
const QUERY_CACHE_KEY = "amoozim-query-cache";

function clearAllCaches(): Promise<void> {
  return new Promise((resolve) => {
    try {
      localStorage.removeItem(QUERY_CACHE_KEY);
    } catch {
      /* storage unavailable — ignore */
    }

    const req = indexedDB.deleteDatabase(IMAGE_CACHE_DB);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

export default function SettingsPage() {
  const {
    isFullscreen,
    bottomBarStyle,
    setFullscreen,
    setBottomBarStyle,
  } = useSettingStore();

  const isAndroid = isAndroidMiniapp();

  const [resetOpen, setResetOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onConfirmReset = async () => {
    if (busy) return;
    setBusy(true);
    localStorage.removeItem("app-settings");
    window.location.reload();
  };

  const onConfirmClearCache = async () => {
    if (busy) return;
    setBusy(true);
    await clearAllCaches();
    toast.success("حافظه‌نهان پاک شد");
    setTimeout(() => window.location.reload(), 400);
  };

  return (
    <div className="content-container min-h-svh bg-secondary-background pb-10">
      <TopBar title={"تنظیمات"} />

      <Section title="نمایش">
        {isAndroid ? (
          <>
            <Row
              icon="fullscreen-setting"
              iconClassName="bg-indigo-500"
              title="حالت تمام صفحه"
              trailing={
                <Switch
                  checked={isFullscreen}
                  onChange={() => setFullscreen(!isFullscreen)}
                />
              }
            />
            <RowDivider inset />
          </>
        ) : null}

        <Row
          icon="home"
          iconClassName="bg-green-500"
          title="نوار پایین شناور"
          subtitle="حالت مدرن و شناور"
          trailing={
            <Switch
              checked={bottomBarStyle === "floating"}
              onChange={() =>
                setBottomBarStyle(
                  bottomBarStyle === "floating" ? "classic" : "floating",
                )
              }
            />
          }
        />
      </Section>

      <Section title="حافظه و تنظیمات">
        <Row
          icon="remove-cache"
          iconClassName="bg-orange-500"
          title="پاک‌سازی حافظه‌نهان"
          subtitle="تصاویر و دیتاهای کش‌شده پاک می‌شود."
          trailing={<Chevron />}
          onClick={() => setClearOpen(true)}
        />
        <RowDivider inset />
        <Row
          icon="rotate-right"
          iconClassName="bg-red-500"
          title={<span className="text-danger">بازنشانی تنظیمات</span>}
          subtitle="تمام تنظیمات به حالت پیش‌فرض برمی‌گردد."
          trailing={<Chevron />}
          onClick={() => setResetOpen(true)}
        />
      </Section>

      <ConfirmDrawer
        open={clearOpen}
        onOpenChange={setClearOpen}
        title="پاک‌سازی حافظه‌نهان"
        description="حافظه‌نهان تصاویر و داده‌های ذخیره‌شده‌ی شما حذف می‌شود و برنامه بازنشانی خواهد شد. ادامه می‌دهید؟"
        confirmLabel="پاک کن"
        onConfirm={onConfirmClearCache}
        loading={busy}
        variant="danger"
      />

      <ConfirmDrawer
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="بازنشانی تنظیمات"
        description="تمام تنظیمات نمایش به حالت پیش‌فرض برمی‌گردد. ادامه می‌دهید؟"
        confirmLabel="بازنشانی"
        onConfirm={onConfirmReset}
        loading={busy}
        variant="danger"
      />
    </div>
  );
}
